import { t } from '../../i18n/index.js';
import { useState } from 'react';
import { useSession } from '../../lib/session.js';
import { useApiData } from '../../lib/use-data.js';
import { formatDate, formatNumber } from '../../lib/format.js';
import { DataTable, type Column } from '../../patterns/DataTable.js';
import { useTableView } from '../../patterns/table-view.js';
import { PageHeader } from '../../patterns/PageHeader.js';
import { Badge } from '../../ui/Badge.js';
import { Banner } from '../../ui/Banner.js';
import { Button } from '../../ui/Button.js';
import { navigate } from '../../app/routes.js';
import { CreateTenantDialog } from './CreateTenantDialog.js';
import type { TenantSummary } from '../../lib/api.js';

/**
 * Every account, and nothing about anybody's money.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHAT THIS PAGE DELIBERATELY DOES NOT SHOW
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Revenue. Settlements. Anything a seller would recognise as their own
 * figures. The endpoint behind this returns metadata only and a test pins its
 * exact key set, so the restriction is enforced rather than observed.
 *
 * It is worth knowing why, because "just add the revenue column" is a
 * reasonable-sounding request. Reading a tenant's books requires a tenant
 * token, which an admin does not hold and cannot mint — that is the whole
 * separation. The moment this page shows business data, the admin console has
 * become a cross-tenant reporting engine with a session check in front of it,
 * and the separation is a comment rather than a property.
 *
 * `events` and `members` are the exception that proves it: counts of rows, not
 * their contents. They answer "is this account alive", which is an operational
 * question, without answering anything about what it sells.
 */
export function TenantsPage() {
  const { api, me } = useSession();
  const { data, loading, error, reload } = useApiData(() => api.tenants(), [api]);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<string | null>(null);

  /*
    `admin` and above. Absent rather than disabled for `support`, the same rule
    the purge button follows: a disabled control invites "why not", an absent
    one does not claim the action exists. The platform refuses either way.
  */
  const mayCreate = me?.role === 'admin' || me?.role === 'super_admin';

  const columns: Column<TenantSummary>[] = [
    {
      id: 'name',
      header: t('tenants.name'),
      /*
        `sort` returns the raw name, never the rendered cell.

        The house rule, and it exists because sorting a formatted value sorts
        the formatting: a column rendered as a badge sorts by the badge's text,
        and a number rendered as "1,204" sorts as a string, where "1,204" comes
        before "9".
      */
      sort: (r: TenantSummary) => r.name.toLowerCase(),
      search: (r: TenantSummary) => `${r.name} ${r.slug}`,
      cell: (r: TenantSummary) => (
        <div className="flex flex-col">
          <span className="font-medium text-primary">{r.name}</span>
          <span className="text-sm text-tertiary">{r.slug}</span>
        </div>
      ),
    },
    {
      id: 'plan',
      header: t('tenants.plan'),
      sort: (r: TenantSummary) => r.planCode ?? '',
      search: (r: TenantSummary) => r.planCode,
      cell: (r: TenantSummary) => (
        <span className="text-secondary">{r.planCode ?? t('common.noValue')}</span>
      ),
    },
    {
      id: 'status',
      header: t('tenants.status'),
      sort: (r: TenantSummary) => r.status,
      cell: (r: TenantSummary) => (
        <Badge intent={r.status === 'active' ? 'success' : 'neutral'}>{r.status}</Badge>
      ),
    },
    {
      id: 'members',
      header: t('tenants.members'),
      align: 'right',
      nowrap: true,
      sort: (r: TenantSummary) => r.members,
      cell: (r: TenantSummary) => formatNumber(r.members),
    },
    {
      id: 'events',
      header: t('tenants.events'),
      align: 'right',
      nowrap: true,
      sort: (r: TenantSummary) => r.events,
      cell: (r: TenantSummary) => formatNumber(r.events),
    },
    {
      id: 'lastEventAt',
      header: t('tenants.lastActivity'),
      nowrap: true,
      sort: (r: TenantSummary) => r.lastEventAt ?? '',
      /*
        An account with no events shows "nothing yet" rather than a dash.

        A dash reads as missing data. This is not missing — it is a real and
        interesting fact about a connected account, and the three empty ones on
        this platform exist precisely so somebody can watch them fill.
      */
      cell: (r: TenantSummary) =>
        r.lastEventAt ? (
          formatDate(r.lastEventAt)
        ) : (
          <span className="text-tertiary">{t('tenants.noActivity')}</span>
        ),
    },
  ];

  const view = useTableView(data ?? [], {
    columns,
    searchPlaceholder: t('tenants.search'),
    itemNoun: t('tenants.noun'),
    initialSort: { columnId: 'name', direction: 'asc' },
  });

  return (
    <>
      <PageHeader
        title={t('tenants.title')}
        description={t('tenants.description')}
        actions={
          mayCreate ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              {t('tenants.create')}
            </Button>
          ) : null
        }
      />

      {created ? (
        <Banner intent="success" title={t('tenants.createdTitle')}>
          {t('tenants.createdBody', { name: created })}
        </Banner>
      ) : null}
      <DataTable
        view={view}
        loading={loading}
        error={error}
        onRetry={reload}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate({ name: 'tenant', id: r.id })}
        /*
          `emptyReason` is required when a table can be empty, and this one is
          honest: no accounts because none exist, not because a filter hid
          them. The filtered case is the table's own and says something else.
        */
        emptyReason="nothing-yet"
        emptyTitle={t('tenants.emptyTitle')}
        emptyDescription={t('tenants.emptyBody')}
      />

      {creating ? (
        <CreateTenantDialog
          onCancel={() => setCreating(false)}
          onCreate={async (input) => {
            await api.createTenant(input);
            setCreated(input.name);
            setCreating(false);
            /*
              Reloaded rather than pushed onto the list locally. The row carries
              counts and a created-at the platform decides, and a locally
              assembled one would be subtly different from every other row until
              somebody refreshed.
            */
            void reload();
          }}
        />
      ) : null}
    </>
  );
}
